import { FREE_PLAN_ID } from "@Common/constants";
import { authClient } from "@Src/authProvider";
import { httpClient } from "@Src/dataProvider";
import {
	getStripeDashboardUrl,
	getStripePricingTableId,
	getStripePublishableKey,
} from "@Src/external/stripe";
import {
	Box,
	Button,
	Card,
	CardContent,
	CircularProgress,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

interface CheckoutFormProps {
	currentPlanId: number;
}

const StripeDashboardLink = () => {
	const [loading, setLoading] = useState(false);

	const handleOpenPortal = async () => {
		setLoading(true);
		try {
			const response = await httpClient("/settings/settings/billing-portal", {
				method: "POST",
				body: JSON.stringify({ return_url: window.location.href }),
			});

			if (response.json?.url) {
				window.location.href = response.json.url;
			} else {
				// Fallback to static URL
				window.location.href = getStripeDashboardUrl();
			}
		} catch (_error) {
			// Silently fall back to static URL on error
			window.location.href = getStripeDashboardUrl();
		} finally {
			setLoading(false);
		}
	};

	return (
		<Box sx={{ maxWidth: 600, margin: "20px auto" }}>
			<Card>
				<CardContent>
					<Typography variant="h5" component="h2" gutterBottom>
						Manage Your Subscription
					</Typography>
					<Typography variant="body1" sx={{ mt: 2, mb: 3 }}>
						You can manage your subscription, update payment methods, and view
						billing history in the Stripe customer portal.
					</Typography>
					<Button
						variant="contained"
						color="primary"
						onClick={handleOpenPortal}
						disabled={loading}
						sx={{ mt: 1 }}
					>
						{loading ? (
							<>
								<CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
								Opening Portal...
							</>
						) : (
							"Open Stripe Customer Portal"
						)}
					</Button>
				</CardContent>
			</Card>
		</Box>
	);
};

export const CheckoutForm = ({ currentPlanId }: CheckoutFormProps) => {
	const { data: session } = authClient.useSession();
	const [scriptLoaded, setScriptLoaded] = useState(false);

	useEffect(() => {
		// Check if script is already loaded
		const existingScript = document.querySelector(
			'script[src="https://js.stripe.com/v3/pricing-table.js"]',
		);
		if (existingScript) {
			setScriptLoaded(true);
			return;
		}

		// Dynamically load Stripe Pricing Table script
		const script = document.createElement("script");
		script.src = "https://js.stripe.com/v3/pricing-table.js";
		script.async = true;
		script.onload = () => setScriptLoaded(true);
		script.onerror = () => {
			console.error("Failed to load Stripe Pricing Table script");
		};

		document.body.appendChild(script);

		return () => {
			// Cleanup: remove script when component unmounts
			if (script.parentNode) {
				script.parentNode.removeChild(script);
			}
		};
	}, []);

	// If user is on a paid plan, show Stripe dashboard link instead
	if (currentPlanId !== FREE_PLAN_ID) {
		return <StripeDashboardLink />;
	}

	// Free plan users see the pricing table
	if (!scriptLoaded) {
		return <div>Loading checkout...</div>;
	}

	const publishableKey = getStripePublishableKey();
	const pricingTableId = getStripePricingTableId();

	return (
		<div id="checkout">
			<stripe-pricing-table
				pricing-table-id={pricingTableId}
				publishable-key={publishableKey}
				client-reference-id={session?.user?.id}
				customer-email={session?.user?.email}
			/>
		</div>
	);
};
